import React, { useEffect, useState } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { TransportDataForm, TransportNode } from '../../../../app/models/Transport';
import { Vehicle } from '../../../../app/models/Vehicle';
import { Location } from '../../../../app/models/Location';
import { Delivery } from '../../../../app/models/Delivery';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import useVehicleService from '../../../../app/services/hooks/useVehicleService';
import useLocationService from '../../../../app/services/hooks/useLocationService';
import useTransportService from '../../../../app/services/hooks/useTransportService';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import { VehicleFilters } from '../../../../app/models/Vehicle';
import { LocationFilters } from '../../../../app/models/Location';
import { DeliveryFilters } from '../../../../app/models/Delivery';
import DeliverySelectionModal from './DeliverySelectionModal';
import { momentParseDateToInput } from '../../../../helpers';

type StatusChoice = { id: string | number; name: string };

type Props = {
    transport: TransportDataForm;
    setTransport: (transport: TransportDataForm) => void;
    errorFields?: any;
    is_edit?: boolean;
    statusChoices?: StatusChoice[];
    onStatusChange?: (newStatus: string, label: string) => void;
    /** Pestaña activa (nodes | deliveries). Si se pasa, las pestañas se sincronizan con la URL. */
    activeTabKey?: string;
    onTabChange?: (key: string) => void;
    /** Navegar a la página de edición de una entrega (para confirmar entrega). */
    onNavigateToDelivery?: (deliveryId: number) => void;
};

const TransportForm = ({ transport, setTransport, errorFields, is_edit, statusChoices = [], onStatusChange, activeTabKey, onTabChange, onNavigateToDelivery }: Props) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();
    const { getVehicles } = useVehicleService();
    const { getLocations } = useLocationService();
    const { getAvailableDeliveries } = useTransportService();

    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [availableDeliveries, setAvailableDeliveries] = useState<Delivery[]>([]);
    const [showDeliveryModal, setShowDeliveryModal] = useState(false);
    const [selectedDeliveries, setSelectedDeliveries] = useState<Delivery[]>([]);
    const [internalTabKey, setInternalTabKey] = useState('nodes');

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields, setErrors]);

    // Cargar vehículos, ubicaciones y entregas al montar el componente
    useEffect(() => {
        loadVehicles();
        loadLocations();
        loadAvailableDeliveries();
    }, []);

    const loadVehicles = () => {
        const vehicleFilter: VehicleFilters = {
            page: 1,
            per_page: 1000, // Cargar todos los vehículos activos
            sort: 'id',
            order: 'asc',
            patent: '',
            vehicle_type: ''
        };

        getVehicles(vehicleFilter, {
            onSuccess: (response: ServiceResponse) => {
                setVehicles(response.data.vehicles || []);
            },
            onError: (response: ServiceResponse) => {
                console.error('Error al cargar vehículos:', response.message);
            }
        });
    };

    const loadLocations = () => {
        // Primero intentar cargar solo activos, si no hay resultados, cargar todos
        const locationFilter: LocationFilters = {
            page: 1,
            per_page: 1000,
            sort: 'id',
            order: 'asc',
            is_active: true
        };

        getLocations(locationFilter, {
            onSuccess: (response: ServiceResponse) => {
                const loadedLocations = response.data.locations || [];
                console.log('Locations activos cargados:', loadedLocations.length);
                
                // Si no hay ubicaciones activas, intentar cargar todas
                if (loadedLocations.length === 0) {
                    const allLocationsFilter: LocationFilters = {
                        page: 1,
                        per_page: 1000,
                        sort: 'id',
                        order: 'asc'
                    };
                    
                    getLocations(allLocationsFilter, {
                        onSuccess: (allResponse: ServiceResponse) => {
                            console.log('Todas las locations cargadas:', allResponse.data.locations?.length || 0);
                            setLocations(allResponse.data.locations || []);
                        },
                        onError: (errorResponse: ServiceResponse) => {
                            console.error('Error al cargar todas las ubicaciones:', errorResponse.message);
                            setLocations([]);
                        }
                    });
                } else {
                    setLocations(loadedLocations);
                }
            },
            onError: (response: ServiceResponse) => {
                console.error('Error al cargar ubicaciones activas:', response.message);
                // Intentar cargar todas si falla el filtro de activos
                const allLocationsFilter: LocationFilters = {
                    page: 1,
                    per_page: 1000,
                    sort: 'id',
                    order: 'asc'
                };
                
                getLocations(allLocationsFilter, {
                    onSuccess: (allResponse: ServiceResponse) => {
                        setLocations(allResponse.data.locations || []);
                    },
                    onError: () => {
                        setLocations([]);
                    }
                });
            }
        });
    };

    const handleChangeSelectTwo = (name: string, value: number | string | null | undefined) => {
        setTransport({ ...transport, [name]: value ?? null });
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setTransport({ ...transport, [name]: value || null });
    };

    // Manejo de nodos
    const addNode = () => {
        const currentNodes = transport.nodes || [];
        const maxSequence = currentNodes.length > 0 
            ? Math.max(...currentNodes.map(n => n.sequence || 0), 0)
            : 0;
        
        const newNode: TransportNode = {
            location_id: null,
            sequence: maxSequence + 1
        };

        setTransport({
            ...transport,
            nodes: [...currentNodes, newNode]
        });
    };

    const removeNode = (index: number) => {
        const currentNodes = transport.nodes || [];
        const updatedNodes = currentNodes.filter((_, i) => i !== index);
        
        // Reordenar secuencias
        const reorderedNodes = updatedNodes.map((node, i) => ({
            ...node,
            sequence: i + 1
        }));

        setTransport({
            ...transport,
            nodes: reorderedNodes
        });
    };

    const updateNode = (index: number, field: keyof TransportNode, value: any) => {
        const currentNodes = transport.nodes || [];
        const updatedNodes = currentNodes.map((node, i) => {
            if (i === index) {
                return { ...node, [field]: value };
            }
            return node;
        });

        setTransport({
            ...transport,
            nodes: updatedNodes
        });
    };

    const moveNodeUp = (index: number) => {
        if (index === 0) return;
        
        const currentNodes = transport.nodes || [];
        const updatedNodes = [...currentNodes];
        [updatedNodes[index - 1], updatedNodes[index]] = [updatedNodes[index], updatedNodes[index - 1]];
        
        // Actualizar secuencias
        const reorderedNodes = updatedNodes.map((node, i) => ({
            ...node,
            sequence: i + 1
        }));

        setTransport({
            ...transport,
            nodes: reorderedNodes
        });
    };

    const moveNodeDown = (index: number) => {
        const currentNodes = transport.nodes || [];
        if (index === currentNodes.length - 1) return;
        
        const updatedNodes = [...currentNodes];
        [updatedNodes[index], updatedNodes[index + 1]] = [updatedNodes[index + 1], updatedNodes[index]];
        
        // Actualizar secuencias
        const reorderedNodes = updatedNodes.map((node, i) => ({
            ...node,
            sequence: i + 1
        }));

        setTransport({
            ...transport,
            nodes: reorderedNodes
        });
    };

    const loadAvailableDeliveries = () => {
        const deliveryFilter: DeliveryFilters & { transport_id?: number } = {
            page: 1,
            per_page: 1000,
            sort: 'id',
            order: 'desc',
            transport_id: transport.id || undefined
        };

        getAvailableDeliveries(deliveryFilter, {
            onSuccess: (response: ServiceResponse) => {
                setAvailableDeliveries(response.data.deliveries || []);
            },
            onError: (response: ServiceResponse) => {
                console.error('Error al cargar entregas disponibles:', response.message);
                setAvailableDeliveries([]);
            }
        });
    };

    // Cargar entregas seleccionadas cuando cambia transport.delivery_ids o cuando se carga el transporte
    useEffect(() => {
        if (transport.delivery_ids && transport.delivery_ids.length > 0) {
            // Si tenemos entregas disponibles cargadas, filtrarlas
            if (availableDeliveries.length > 0) {
                const deliveries = availableDeliveries.filter(d => 
                    transport.delivery_ids?.includes(d.id!)
                );
                setSelectedDeliveries(deliveries);
            } else {
                // Si no hay entregas disponibles cargadas pero hay IDs, recargar
                loadAvailableDeliveries();
            }
        } else {
            setSelectedDeliveries([]);
        }
    }, [transport.delivery_ids]);

    // Actualizar entregas seleccionadas cuando se cargan las disponibles
    useEffect(() => {
        if (transport.delivery_ids && transport.delivery_ids.length > 0 && availableDeliveries.length > 0) {
            const deliveries = availableDeliveries.filter(d => 
                transport.delivery_ids?.includes(d.id!)
            );
            setSelectedDeliveries(deliveries);
        }
    }, [availableDeliveries]);

    const handleDeliverySelection = (deliveryIds: number[]) => {
        setTransport({ ...transport, delivery_ids: deliveryIds });
        setShowDeliveryModal(false);
    };

    const removeDelivery = (deliveryId: number) => {
        const updatedIds = (transport.delivery_ids || []).filter(id => id !== deliveryId);
        setTransport({ ...transport, delivery_ids: updatedIds });
    };

    // Opciones para SelectTwo
    const vehicleOptions = SelectTwoMapperOptions(vehicles, 'id', ['patent']);
    const locationOptions = SelectTwoMapperOptions(locations, 'id', ['name']);

    return (
        <>
            {/* CABECERA: Vehículo y Fecha de Despacho */}
            <div className="mb-3">
                <h5 className="mb-3">Datos del Transporte</h5>
                
                {/* Campo Vehículo */}
                <div className="mb-2 row needs-validation">
                    <label className="col-md-3 col-form-label">Vehículo</label>
                    <div className="col-md-9">
                        <SelectTwo
                            name="vehicle_id"
                            inputValue={transport.vehicle_id || null}
                            options={vehicleOptions}
                            onChange={(option) => handleChangeSelectTwo('vehicle_id', option?.value || null)}
                            placeholder="Seleccione un vehículo"
                            hasError={!!fieldHasError('vehicle_id')}
                            onFocus={() => onFocusRemove('vehicle_id')}
                            isMulti={false}
                            isClearable={true}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('vehicle_id')}
                        </div>
                    </div>
                </div>

                {/* Campo Fecha de Despacho */}
                <div className="mb-2 row needs-validation">
                    <label className="col-md-3 col-form-label">Fecha de Despacho</label>
                    <div className="col-md-9">
                        <input
                            name="dispatch_date"
                            id="dispatch_date"
                            type="date"
                            className={`form-control ${fieldHasError('dispatch_date')}`}
                            value={transport.dispatch_date || ''}
                            onChange={handleChange}
                            onFocus={() => onFocusRemove('dispatch_date')}
                        />
                        <div className="invalid-feedback" style={{ display: 'flex' }}>
                            {fieldErrorMessage('dispatch_date')}
                        </div>
                    </div>
                </div>

                {/* Fila Estado: Fecha inicio, Estado, Fecha fin (solo en edición con statusChoices) */}
                {is_edit && statusChoices.length > 0 && (
                    <div className="mb-2 row mt-3">
                        <div className="col-md-4">
                            <label className="col-form-label">Fecha de inicio</label>
                            <input
                                name="start_date"
                                type="date"
                                className="form-control"
                                value={transport.start_date || ''}
                                readOnly
                                disabled
                                title="Se registra al cambiar a En tránsito"
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="col-form-label">Estado</label>
                            <SelectTwo
                                name="status"
                                id="transport_status"
                                inputValue={transport.status ?? null}
                                options={statusChoices.map((s) => ({ value: s.id, label: String(s.name) }))}
                                onChange={(option: any) => {
                                    if (!option?.value || option.value === transport.status) return;
                                    onStatusChange?.(String(option.value), String(option.label));
                                }}
                                placeholder="Seleccione estado"
                                isMulti={false}
                                isClearable={false}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="col-form-label">Fecha de fin</label>
                            <input
                                name="end_date"
                                type="date"
                                className="form-control"
                                value={transport.end_date || ''}
                                readOnly
                                disabled
                                title="Se registra al cambiar a Finalizado"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* PESTAÑAS: Nodos y Entregas (sincronizadas con URL en edición cuando se pasan activeTabKey y onTabChange) */}
            <Tabs
                id="transport-tabs"
                className="mb-3"
                activeKey={activeTabKey !== undefined ? activeTabKey : internalTabKey}
                onSelect={(k) => {
                    const key = k ?? 'nodes';
                    setInternalTabKey(key);
                    onTabChange?.(key);
                }}
            >
                {/* PESTAÑA 1: NODOS */}
                <Tab eventKey="nodes" title="Nodos">

                    <div className="mb-2 row transport-nodes-section" style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '100%' }}>
                        <div className="col-12" style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0, maxHeight: '100%' }}>
                            <div className="d-flex justify-content-between align-items-center mb-2" style={{ flexShrink: 0 }}>
                                <label className="col-form-label mb-0">Nodos (Ubicaciones)</label>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={addNode}
                                >
                                    <i className="mdi mdi-plus"></i> Agregar Nodo
                                </button>
                            </div>

                    {transport.nodes && transport.nodes.length > 0 ? (
                        <div className="table-responsive transport-nodes-table" style={{ flex: '1 1 auto', minHeight: 0, maxHeight: '100%' }}>
                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th style={{ width: '50px' }}>Orden</th>
                                        <th>Ubicación</th>
                                        <th style={{ width: '150px' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transport.nodes.map((node, index) => (
                                        <tr key={index}>
                                            <td className="text-center">
                                                <span className="badge bg-primary">{node.sequence || index + 1}</span>
                                            </td>
                                            <td>
                                                <SelectTwo
                                                    name={`node_location_${index}`}
                                                    inputValue={node.location_id || null}
                                                    options={locationOptions}
                                                    onChange={(option) => updateNode(index, 'location_id', option?.value || null)}
                                                    placeholder="Seleccione una ubicación"
                                                    hasError={false}
                                                    isMulti={false}
                                                    isClearable={true}
                                                />
                                            </td>
                                            <td>
                                                <div className="btn-group btn-group-sm" role="group">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => moveNodeUp(index)}
                                                        disabled={index === 0}
                                                        title="Mover arriba"
                                                    >
                                                        <i className="mdi mdi-arrow-up"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => moveNodeDown(index)}
                                                        disabled={index === (transport.nodes?.length || 0) - 1}
                                                        title="Mover abajo"
                                                    >
                                                        <i className="mdi mdi-arrow-down"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-danger"
                                                        onClick={() => removeNode(index)}
                                                        title="Eliminar"
                                                    >
                                                        <i className="mdi mdi-delete"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="alert alert-info">
                            <i className="mdi mdi-information"></i> No hay nodos agregados. Haga clic en &quot;Agregar Nodo&quot; para comenzar.
                        </div>
                        )}
                        </div>
                    </div>
                </Tab>

                {/* PESTAÑA 2: ENTREGAS */}
                <Tab eventKey="deliveries" title="Entregas">
                    <div className="mb-2 row">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <label className="col-form-label mb-0">Entregas Asignadas</label>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => setShowDeliveryModal(true)}
                                >
                                    <i className="mdi mdi-plus"></i> Buscar y Agregar Entregas
                                </button>
                            </div>

                            {selectedDeliveries.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '80px' }}>ID</th>
                                                <th>Pedido</th>
                                                <th>Cliente</th>
                                                <th>Destino</th>
                                                <th>Estado</th>
                                                <th style={{ width: '100px' }}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedDeliveries.map((delivery) => (
                                                <tr key={delivery.id}>
                                                    <td>{delivery.id || '-'}</td>
                                                    <td>{delivery.sales_order || `Pedido #${delivery.sales_order_id}`}</td>
                                                    <td dangerouslySetInnerHTML={{ __html: delivery.client || '' }} />
                                                    <td>{delivery.destination || '-'}</td>
                                                    <td dangerouslySetInnerHTML={{ __html: delivery.status_display || delivery.status || '-' }} />
                                                    <td>
                                                        <div className="btn-group btn-group-sm" role="group">
                                                            {onNavigateToDelivery && delivery.id && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-primary"
                                                                    onClick={() => onNavigateToDelivery(delivery.id!)}
                                                                    title="Ver entrega / Confirmar entrega"
                                                                >
                                                                    <i className="mdi mdi-eye"></i> Ver entrega
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => removeDelivery(delivery.id!)}
                                                                title="Quitar entrega"
                                                            >
                                                                <i className="mdi mdi-delete"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="alert alert-info">
                                    <i className="mdi mdi-information"></i> No hay entregas asignadas. Haga clic en &quot;Buscar y Agregar Entregas&quot; para comenzar.
                                </div>
                            )}
                        </div>
                    </div>
                </Tab>
            </Tabs>

            {/* Modal para seleccionar entregas */}
            <DeliverySelectionModal
                show={showDeliveryModal}
                handleClose={() => setShowDeliveryModal(false)}
                transportId={transport.id}
                selectedDeliveryIds={transport.delivery_ids || []}
                onSelect={handleDeliverySelection}
            />
        </>
    );
};

export default TransportForm;
